import type { AuditTimelineEntry } from '../../types/auditTimeline';
import { formatRelativeTime } from '../../lib/relativeTime';
import { describeAuditEntry } from './describeAuditEntry';

// Presentational audit timeline (P2b-2 §7.5.3). Pure props-in / DOM-out —
// the caller owns data fetching (see useRowAuditLog for the row case).
//
// When Campaign / Account history land in P4, those surfaces wire their
// own hooks to the same component without changes here. That's why
// entityType / entityId live on each entry rather than as component
// props: the timeline can render mixed-entity feeds down the road with
// zero refactor.
//
// UX rules:
//   - Newest-first (hook orders the fetch; the component doesn't re-sort)
//   - Relative time on the left with full-ISO tooltip for disambiguation
//   - Context-specific summaries for domain verbs (opt-out, refresh, etc.)
//   - Multi-field updates render as a summary + bulleted per-field list
//   - Arrays + nested objects summarise rather than deep-diff (audit doc
//     retains the full data for anyone inspecting server-side)

type Props = {
  entries: AuditTimelineEntry[] | undefined;
  isLoading: boolean;
  error: string | null;
  emptyLabel?: string;
};

export function AuditTimeline({ entries, isLoading, error, emptyLabel }: Props) {
  if (isLoading) {
    return <p className="text-sm text-slate-400 italic">Loading history…</p>;
  }
  if (error) {
    return (
      <p className="text-sm text-red-600">
        Failed to load history: {error}
      </p>
    );
  }
  if (!entries || entries.length === 0) {
    return (
      <p className="text-sm text-slate-400 italic">
        {emptyLabel ?? 'No history yet.'}
      </p>
    );
  }
  return (
    <ol className="space-y-4">
      {entries.map((e) => (
        <AuditEntryRow key={e.id} entry={e} />
      ))}
    </ol>
  );
}

function AuditEntryRow({ entry }: { entry: AuditTimelineEntry }) {
  const desc = describeAuditEntry(entry);
  return (
    <li className="grid grid-cols-[140px_1fr] gap-3 text-sm">
      <time
        dateTime={entry.timestamp}
        title={new Date(entry.timestamp).toLocaleString()}
        className="text-xs text-slate-500 pt-0.5"
      >
        {formatRelativeTime(entry.timestamp)}
      </time>
      <div className="text-slate-800">
        <p>{desc.summary}</p>
        {desc.bullets.length > 0 && (
          <ul className="mt-1 list-disc list-inside space-y-0.5 text-slate-600 text-xs">
            {desc.bullets.map((b, i) => (
              <li key={i}>{b}</li>
            ))}
          </ul>
        )}
      </div>
    </li>
  );
}
